/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from 'react'
import styles from './styles.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { addNewPatient, updatePatient } from '../../../../../Redux/patients/patientsSlice'
import { getAllUsers } from '../../../../../Redux/users/usersSlice'
import { addNewAppointmentFromFile } from '../../../../../Redux/appointments/appointmentSlice'
import SearchableOperationEntry from './SearchableOperationEntry'
import { useTranslation } from 'react-i18next'
import SVGMouth from '../../../../pages/PatientPage/components/OrganData/SVGMouth'
import { arrayOfTimesGeneral } from '../../../../pages/Appointments/timeLines'
import { v4 as uuidv4 } from 'uuid'
import printPatientPDF from '../../../../../utils/printPatientPDF'
import { collection, query, getDocs } from 'firebase/firestore'
import { firestore } from '../../../../../config/firebase'

const handleTextareaInput = (e) => {
  const lineHeight = 24; // përafërsisht për font-size 1.1-1.3rem
  e.target.style.height = 'auto';
  const maxHeight = lineHeight * 40;
  e.target.style.height = Math.min(e.target.scrollHeight, maxHeight) + 'px';
};

const index = ({ close, userdata }) => {
  const { t } = useTranslation() || ((text) => text)
  const auth = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const { profile, user } = auth.user || {}
  const usedName = 'Dr, ' + (profile?.displayName || user?.displayName || 'No name')
  const signature = profile?.signature || ''
  const { operations } = useSelector((state) => state.operations)
  const { users, loading: usersLoading } = useSelector((state) => state.users)
  const { patients } = useSelector((state) => state.patients)
  const { appointments } = useSelector((state) => state.appointments)
  const isAdmin = profile?.role === 'admin'
  const [services, setServices] = useState([{ id: 1, name: '', price: 0 }])
  const [showAdditional, setShowAdditional] = useState(false)
  const [selectedTeeth, setSelectedTeeth] = useState([])
  const [selectedType, setSelectedType] = useState('all')
  const [selectedRoom, setSelectedRoom] = useState('')
  const [serviceMenuOpen, setServiceMenuOpen] = useState({})
  const [serviceSearchInput, setServiceSearchInput] = useState('')
  const [selectedDoctor, setSelectedDoctor] = useState('')
  const [displayUsers, setDisplayUsers] = useState([])
  const menuRefs = useRef({})
  
  // State for cost-related fields
  const [serviceCostValue, setServiceCostValue] = useState(0)
  const [patientPayValue, setPatientPayValue] = useState(0)
  const [differenceValue, setDifferenceValue] = useState(0)
  const [isFreeService, setIsFreeService] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [showResults, setShowResults] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const searchRef = useRef(null)

  // State për akordeonët e shërbimeve të pacientit ekzistues
  const [expandedServices, setExpandedServices] = useState({})

  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    dob: '',
    email: '',
    phone: '',
    nid: '',
    location: '',
    main_issue: '',
    examination: '',
    ekg: '',
    feedback: '',
    rtg_pa_pulmo: '',
    ta: '',
    spo2: '',
    dg: ''
  })

  const { id, nid, phone, location, email, dob, full_name, category, attachements, teeth_list, room, ...rest } = userdata || {
    id: null,
    nid: '',
    phone: '',
    location: '',
    photoURL: '',
    email: '',
    dob: '',
    full_name: '',
    category: '',
    operations: [],
    teeth_list: [],
    room: ''
  }

  // Add selectedPatient state
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Set initial values from user data if available
  React.useEffect(() => {
    if (userdata) {
      if (teeth_list) setSelectedTeeth(teeth_list);
      if (room) setSelectedRoom(room);
      
      // If we have userdata with an id, this is an edit operation
      if (userdata.id) {
        setSelectedPatient(userdata);
        setFormData({
          full_name: userdata.full_name || '',
          dob: userdata.dob ? new Date(userdata.dob).getFullYear().toString() : '',
          email: userdata.email || '',
          phone: userdata.phone || '',
          nid: userdata.nid || '',
          location: userdata.location || '',
          main_issue: userdata.main_issue || '',
          examination: userdata.examination || '',
          ekg: userdata.ekg || '',
          feedback: userdata.feedback || '',
          rtg_pa_pulmo: userdata.rtg_pa_pulmo || '',
          ta: userdata.ta || '',
          spo2: userdata.spo2 || '',
          dg: userdata.dg || ''
        });
      }
    }
  }, [userdata, teeth_list, room]);

  // Add useEffect to fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (isAdmin) {
          // For admin users, use the Redux action
          await dispatch(getAllUsers()).unwrap()
        } else {
          // For standard users, try to fetch users directly
          const usersCollection = collection(firestore, 'users')
          const q = query(usersCollection)
          const querySnapshot = await getDocs(q)
          const usersList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          setDisplayUsers(usersList)
        }
      } catch (error) {
        console.error('Error fetching users:', error)
        // If direct fetch fails, at least show the current user
        setDisplayUsers([{
          id: profile?.uid,
          displayName: profile?.displayName,
          role: profile?.role,
          ...profile
        }])
      }
    }
    fetchUsers()
  }, [dispatch, isAdmin, profile])

  // Add debug log for users
  useEffect(() => {
    console.log('Current users in state:', users)
    console.log('Display users:', displayUsers)
  }, [users, displayUsers])

  // Add effect to automatically select Dr. Vera Lleshi for pulmonology
  useEffect(() => {
    if (category === 'pulmonology') {
      const veraDoctor = (isAdmin ? users : displayUsers)?.find(user => 
        user.displayName?.toLowerCase().includes('vera lleshi')
      );
      if (veraDoctor) {
        setSelectedDoctor(veraDoctor.id);
      }
    }
  }, [category, users, displayUsers, isAdmin]);

  const printData = (data) => {
    // Compose the patient and operation data for the PDF
    const { doctor, close, operationData, ...patientData } = data;
    const selectedDoctorData = (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor);
    printPatientPDF({
      patientData: {
        ...patientData,
        rtg_pa_pulmo: patientData.rtg_pa_pulmo || '',
        spo2: patientData.spo2 || ''
      },
      operation: {
        ...operationData,
        op_name: operationData?.services?.[0] || '',
        custom_service_name: operationData?.services?.[0] || '',
        init_date: patientData.appointment_date || new Date().toISOString().split('T')[0],
        xray: patientData.rtg_pa_pulmo || '',
        spo: patientData.spo2 || '',
        doctor_id: selectedDoctor,
        doctor_name: selectedDoctorData?.displayName || 'Dr. No Name'
      },
      doctorName: selectedDoctorData?.displayName || 'Dr. No Name',
      signature: selectedDoctorData?.signature || ''
    });
  }

  const onToothclick = (toothNumber) => {
    let newTeethArray = [...selectedTeeth];
    if (newTeethArray.includes(toothNumber)) {
      newTeethArray = newTeethArray.filter((tooth) => tooth !== toothNumber);
    } else {
      newTeethArray.push(toothNumber);
    }
    setSelectedTeeth(newTeethArray);
  }

  const handleClickOutside = (e) => {
    // Check if click is outside all service menus
    const clickedOutsideAllMenus = Object.values(menuRefs.current).every(
      ref => ref.current && !ref.current.contains(e.target)
    )
    if (clickedOutsideAllMenus) {
      setServiceMenuOpen({})
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const filteredServices = operations?.filter((op) => {
    const searchInputLower = serviceSearchInput.toLowerCase()
    const opNameLower = op.op_name?.toLowerCase()
    return (
      op.category === category &&
      (opNameLower?.includes(searchInputLower) || searchInputLower === '')
    )
  })

  const handleServiceSelect = (operation, serviceId) => {
    // Update the service in the services array
    setServices(services.map(service => 
      service.id === serviceId ? { ...service, name: operation.op_name, price: operation.price } : service
    ))
    
    // Close the service menu
    setServiceMenuOpen({})
    
    // Calculate total price from all services
    const updatedServices = services.map(service => 
      service.id === serviceId ? { ...service, name: operation.op_name, price: operation.price } : service
    )
    
    // Update total cost
    const newTotalCost = updatedServices.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0)
    setServiceCostValue(newTotalCost)
    
    // For pulmonology, set patient pay equal to service cost by default
    if (category === 'pulmonology' && !isFreeService) {
      setPatientPayValue(newTotalCost);
      setDifferenceValue(0);
    } else {
      setDifferenceValue(newTotalCost - patientPayValue)
    }
  }

  const handlePatientPayChange = (value) => {
    const patientPay = parseFloat(value) || 0
    setPatientPayValue(patientPay)
    setDifferenceValue(serviceCostValue - patientPay)
  }

  const addNewService = () => {
    setServices([...services, { id: services.length + 1, name: '', price: 0 }])
  }

  const deleteService = (serviceId) => {
    // Don't allow deleting the first service
    if (serviceId === 1) return;
    
    // Remove the service and recalculate total cost
    const updatedServices = services.filter(service => service.id !== serviceId);
    setServices(updatedServices);
    
    // Recalculate total cost
    const newTotalCost = updatedServices.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0);
    setServiceCostValue(newTotalCost);
    
    // Recalculate difference
    setDifferenceValue(newTotalCost - patientPayValue);
  }

  // Add date formatting function
  const formatDateToEuropean = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Add date parsing function
  const parseEuropeanDate = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    if (!day || !month || !year) return '';
    return `${year}-${month}-${day}`;
  };

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Handle patient selection
  const handlePatientSelect = (patient) => {
    setSearchValue(patient.full_name)
    setShowResults(false)
    setSelectedPatient(patient)
    
    // Update form state with patient data - lë bosh fushat e ekzaminimeve mjekësore
    setFormData({
      full_name: patient.full_name || '',
      dob: patient.dob ? new Date(patient.dob).getFullYear().toString() : '',
      email: patient.email || '',
      phone: patient.phone || '',
      nid: patient.nid || '',
      location: patient.location || '',
      main_issue: '', // Bosh
      examination: '', // Bosh
      ekg: '', // Bosh
      feedback: '', // Bosh
      rtg_pa_pulmo: '', // Bosh
      ta: '', // Bosh
      spo2: '', // Bosh
      dg: '' // Bosh
    })

    // Create a new appointment for the selected patient
    if (services.length > 0 && services[0].name) {
      const service = services[0]
      const operation = {
        id: uuidv4(),
        op_name: service.name,
        price: service.price,
        category: category
      }

      const aptObj = {
        date: new Date().toISOString().split('T')[0],
        from: new Date().getHours(),
        to: new Date().getHours() + 1,
        name: service.name,
        category: category,
        operation_id: operation.id,
        operation_name: service.name,
        patient_id: patient.id,
        patient_name: patient.full_name,
        doctor_id: selectedDoctor,
        doctor_name: (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor)?.displayName || 'Dr. No Name',
        service_cost: service.price,
        patient_pay: category === 'pulmonology' && isFreeService ? 0 : service.price,
        difference: 0,
        is_ongoing: true,
        init_date: new Date().toISOString(),
        main_issue: patient.main_issue || ''
      }

      // First update the patient record
      dispatch(updatePatient({
        id: patient.id,
        ...patient,
        operations: [
          ...(patient.operations || []),
          {
            operation_id: operation.id,
            category: category,
            latest: true,
            init_date: new Date().toISOString(),
            teeth_list: selectedTeeth,
            room: selectedRoom,
            feedback: formData.feedback,
            examination: formData.examination,
            therapy: '',
            service_cost: service.price,
            patient_pay: category === 'pulmonology' && isFreeService ? 0 : service.price,
            difference: 0,
            main_issue: patient.main_issue || ''
          }
        ]
      }))

      // Then create the appointment
      dispatch(addNewAppointmentFromFile({
        ...aptObj,
        additionalPayload: {
          teeth_list: selectedTeeth,
          room: selectedRoom,
          feedback: formData.feedback,
          examination: formData.examination,
          therapy: '',
          category: category,
          service_cost: service.price,
          patient_pay: category === 'pulmonology' && isFreeService ? 0 : service.price,
          difference: 0,
          main_issue: patient.main_issue || ''
        },
        close: () => {
          // Close the popup after successful appointment creation
          close()
        }
      }))
    }
  }

  // Search patients when input changes
  const handleSearch = (e) => {
    const value = e.target.value
    setSearchValue(value)
    setFormData(prev => ({ ...prev, full_name: value }))
    setSelectedPatient(null)
    
    if (value.length > 0) {
      const filteredPatients = patients.filter(patient => 
        patient.full_name?.toLowerCase().includes(value.toLowerCase())
      )
      setSearchResults(filteredPatients)
      setShowResults(true)
    } else {
      setSearchResults([])
      setShowResults(false)
    }
  }

  const submitData = (e) => {
    e.preventDefault()
    const clickedButton = e?.nativeEvent?.submitter?.id
    const formData = new FormData(e.target)
    
    // Get the date input and convert it to ISO format with validation
    const dobInput = document.getElementById('dob');
    let dobValue = '';
    
    if (dobInput.value) {
      const year = parseInt(dobInput.value);
      if (!isNaN(year) && year >= 1900 && year <= new Date().getFullYear()) {
        const date = new Date(year, 0, 1); // Set to January 1st of the selected year
        if (!isNaN(date.getTime())) {
          dobValue = date.toISOString();
        }
      }
    }
    
    // Get only non-empty services with their prices
    const serviceData = services
      .map(service => ({
        name: formData.get(`service_${service.id}`),
        price: service.price
      }))
      .filter(service => service.name && service.name.trim() !== '')

    // Kontrollo nëse ka shërbim të vlefshëm
    const hasValidService = serviceData.length > 0 && serviceData[0].name && serviceData[0].name.trim() !== '';

    // Calculate total cost from the actual services
    const totalCost = serviceData.reduce((sum, service) => sum + (service.price || 0), 0)
    
    // Krijojmë array-n e pagesave nëse ka pagesë
    let paymentsArray = [];
    if (patientPayValue > 0 && formData.get('appointment_date')) {
      paymentsArray.push({
        amount: patientPayValue,
        value: patientPayValue,
        date: formData.get('appointment_date')
      });
    }
    
    // Calculate total payment from array
    const totalFromPayments = paymentsArray.reduce((sum, payment) => 
      sum + (Number(payment.amount) || Number(payment.value) || 0), 0);
    
    // Combine all service names into a single comma-separated string
    const combinedServices = serviceData.map(service => service.name).join(', ')

    const formObject = {
      email: formData.get('email'),
      full_name: formData.get('full_name'),
      phone: formData.get('phone'),
      location: formData.get('location'),
      nid: formData.get('nid'),
      dob: dobValue,
      category: category,
      room: selectedRoom,
      doctor_id: selectedDoctor,
      appointment_date: formData.get('appointment_date'),
      appointment_time: formData.get('appointment_time'),
      // Add medical examination fields
      rtg_pa_pulmo: formData.get('rtg_pa_pulmo') || '',
      ekg: formData.get('ekg') || '',
      ta: formData.get('ta') || '',
      spo2: formData.get('spo2') || '',
      dg: formData.get('dg') || '',
      operationData: {
        services: [combinedServices],
        service_cost: category === 'pulmonology' && isFreeService ? 0 : serviceCostValue,
        patient_pay: totalFromPayments, // Përdor totalin nga array
        difference: (category === 'pulmonology' && isFreeService ? 0 : serviceCostValue) - totalFromPayments,
        array_of_payments: paymentsArray, // Shto array-n e pagesave
        ekg: formData.get('ekg') || '',
        dg: formData.get('dg') || '',
        xray: formData.get('xray') || '',
        therapy: formData.get('therapy') || '',
        examination: formData.get('examination') || '',
        feedback: formData.get('feedback') || '',
        main_issue: formData.get('main_issue') || '',
        ta: formData.get('ta') || '',
        spo: formData.get('spo') || '',
        teeth_list: selectedTeeth,
        is_free_service: category === 'pulmonology' && isFreeService,
        doctor_id: selectedDoctor,
        doctor_name: (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor)?.displayName || 'Dr. No Name'
      }
    }

    if (selectedPatient && selectedPatient.full_name === formData.get('full_name') && hasValidService) {
      // Update existing patient that was selected from search results
      const operationId = uuidv4()
      
      // Create appointment object
      const aptObj = {
        date: formData.get('appointment_date') || new Date().toISOString().split('T')[0],
        from: formData.get('appointment_time') ? 
          (Number(formData.get('appointment_time').split(':')[0]) + Number(formData.get('appointment_time').split(':')[1]) / 60) : 
          new Date().getHours(),
        to: formData.get('appointment_time') ? 
          (Number(formData.get('appointment_time').split(':')[0]) + Number(formData.get('appointment_time').split(':')[1]) / 60) + 0.5 : 
          new Date().getHours() + 0.5,
        name: combinedServices,
        category: category,
        operation_id: operationId,
        operation_name: combinedServices,
        patient_id: selectedPatient.id,
        patient_name: selectedPatient.full_name,
        doctor_id: selectedDoctor,
        doctor_name: (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor)?.displayName || 'Dr. No Name',
        service_cost: category === 'pulmonology' && isFreeService ? 0 : serviceCostValue,
        patient_pay: totalFromPayments, // Përdor totalin nga array
        difference: (category === 'pulmonology' && isFreeService ? 0 : serviceCostValue) - totalFromPayments,
        array_of_payments: paymentsArray, // Shto array-n e pagesave
        is_ongoing: true,
        init_date: new Date().toISOString(),
        main_issue: formData.get('main_issue') || '',
        payment_date: paymentsArray.length > 0 ? paymentsArray[0].date : null
      }

      // First create the appointment
      dispatch(addNewAppointmentFromFile({
        ...aptObj,
        additionalPayload: {
          teeth_list: selectedTeeth,
          room: selectedRoom,
          feedback: formData.get('feedback') || '',
          examination: formData.get('examination') || '',
          therapy: formData.get('therapy') || '',
          category: category,
          service_cost: category === 'pulmonology' && isFreeService ? 0 : serviceCostValue,
          patient_pay: category === 'pulmonology' && isFreeService ? 0 : patientPayValue,
          difference: category === 'pulmonology' && isFreeService ? 0 : differenceValue,
          main_issue: formData.get('main_issue') || ''
        },
        close: () => {
          // Then update the patient record
          dispatch(
            updatePatient({
              ...selectedPatient,
              ...formObject,
              id: selectedPatient.id,
              operations: [
                ...(selectedPatient.operations || []),
                {
                  operation_id: operationId,
                  category: category,
                  latest: true,
                  init_date: new Date().toISOString(),
                  teeth_list: selectedTeeth,
                  room: selectedRoom,
                  feedback: formData.get('feedback') || '',
                  examination: formData.get('examination') || '',
                  therapy: formData.get('therapy') || '',
                  service_cost: category === 'pulmonology' && isFreeService ? 0 : serviceCostValue,
                  patient_pay: category === 'pulmonology' && isFreeService ? 0 : patientPayValue,
                  difference: category === 'pulmonology' && isFreeService ? 0 : differenceValue,
                  main_issue: formData.get('main_issue') || ''
                }
              ]
            })
          )
          // Handle print if save-print button was clicked
          if (clickedButton === 'save-print') {
            close()
            printData({
              ...formObject,
              doctor: (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor)
            })
          } else {
            close()
          }
        }
      }))
    } else if (id && hasValidService) {
      // Update existing patient (when editing from PatientPage)
      const operationId = uuidv4()
      
      // Create appointment object
      const aptObj = {
        date: formData.get('appointment_date') || new Date().toISOString().split('T')[0],
        from: formData.get('appointment_time') ? 
          (Number(formData.get('appointment_time').split(':')[0]) + Number(formData.get('appointment_time').split(':')[1]) / 60) : 
          new Date().getHours(),
        to: formData.get('appointment_time') ? 
          (Number(formData.get('appointment_time').split(':')[0]) + Number(formData.get('appointment_time').split(':')[1]) / 60) + 0.5 : 
          new Date().getHours() + 0.5,
        name: combinedServices,
        category: category,
        operation_id: operationId,
        operation_name: combinedServices,
        patient_id: id,
        patient_name: formData.get('full_name'),
        doctor_id: selectedDoctor,
        doctor_name: (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor)?.displayName || 'Dr. No Name',
        service_cost: category === 'pulmonology' && isFreeService ? 0 : serviceCostValue,
        patient_pay: totalFromPayments, // Përdor totalin nga array
        difference: (category === 'pulmonology' && isFreeService ? 0 : serviceCostValue) - totalFromPayments,
        array_of_payments: paymentsArray, // Shto array-n e pagesave
        is_ongoing: true,
        init_date: new Date().toISOString(),
        main_issue: formData.get('main_issue') || '',
        payment_date: paymentsArray.length > 0 ? paymentsArray[0].date : null
      }

      // First create the appointment
      dispatch(addNewAppointmentFromFile({
        ...aptObj,
        additionalPayload: {
          teeth_list: selectedTeeth,
          room: selectedRoom,
          feedback: formData.get('feedback') || '',
          examination: formData.get('examination') || '',
          therapy: formData.get('therapy') || '',
          category: category,
          service_cost: category === 'pulmonology' && isFreeService ? 0 : serviceCostValue,
          patient_pay: category === 'pulmonology' && isFreeService ? 0 : patientPayValue,
          difference: category === 'pulmonology' && isFreeService ? 0 : differenceValue,
          main_issue: formData.get('main_issue') || ''
        },
        close: () => {
          // Then update the patient record
          dispatch(
            updatePatient({
              ...userdata,
              ...formObject,
              id: id,
              operations: [
                ...(userdata.operations || []),
                {
                  operation_id: operationId,
                  category: category,
                  latest: true,
                  init_date: new Date().toISOString(),
                  teeth_list: selectedTeeth,
                  room: selectedRoom,
                  feedback: formData.get('feedback') || '',
                  examination: formData.get('examination') || '',
                  therapy: formData.get('therapy') || '',
                  service_cost: category === 'pulmonology' && isFreeService ? 0 : serviceCostValue,
                  patient_pay: category === 'pulmonology' && isFreeService ? 0 : patientPayValue,
                  difference: category === 'pulmonology' && isFreeService ? 0 : differenceValue,
                  main_issue: formData.get('main_issue') || ''
                }
              ]
            })
          )
          // Handle print if save-print button was clicked
          if (clickedButton === 'save-print') {
            close()
            printData({
              ...formObject,
              doctor: (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor)
            })
          } else {
            close()
          }
        }
      }))
    } else if (!id && !selectedPatient && hasValidService) {
      // Create a new patient if no selectedPatient and no id
      const operationId = uuidv4()
      
      // Create appointment object
      const aptObj = {
        date: formData.get('appointment_date') || new Date().toISOString().split('T')[0],
        from: formData.get('appointment_time') ? 
          (Number(formData.get('appointment_time').split(':')[0]) + Number(formData.get('appointment_time').split(':')[1]) / 60) : 
          new Date().getHours(),
        to: formData.get('appointment_time') ? 
          (Number(formData.get('appointment_time').split(':')[0]) + Number(formData.get('appointment_time').split(':')[1]) / 60) + 0.5 : 
          new Date().getHours() + 0.5,
        name: combinedServices,
        category: category,
        operation_id: operationId,
        operation_name: combinedServices,
        patient_name: formData.get('full_name'),
        doctor_id: selectedDoctor,
        doctor_name: (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor)?.displayName || 'Dr. No Name',
        service_cost: category === 'pulmonology' && isFreeService ? 0 : serviceCostValue,
        patient_pay: totalFromPayments, // Përdor totalin nga array
        difference: (category === 'pulmonology' && isFreeService ? 0 : serviceCostValue) - totalFromPayments,
        array_of_payments: paymentsArray, // Shto array-n e pagesave
        is_ongoing: true,
        init_date: new Date().toISOString(),
        main_issue: formData.get('main_issue') || '',
        payment_date: paymentsArray.length > 0 ? paymentsArray[0].date : null
      }

      // Create new patient with operation data
      dispatch(addNewPatient({
        ...formObject,
        operationData: {
          services: [combinedServices],
          service_cost: category === 'pulmonology' && isFreeService ? 0 : serviceCostValue,
          patient_pay: category === 'pulmonology' && isFreeService ? 0 : patientPayValue,
          difference: category === 'pulmonology' && isFreeService ? 0 : differenceValue,
          ekg: formData.get('ekg') || '',
          dg: formData.get('dg') || '',
          xray: formData.get('xray') || '',
          therapy: formData.get('therapy') || '',
          examination: formData.get('examination') || '',
          feedback: formData.get('feedback') || '',
          main_issue: formData.get('main_issue') || '',
          ta: formData.get('ta') || '',
          spo: formData.get('spo') || '',
          teeth_list: selectedTeeth,
          is_free_service: category === 'pulmonology' && isFreeService,
          doctor_id: selectedDoctor,
          doctor_name: (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor)?.displayName || 'Dr. No Name',
          appointment_date: formData.get('appointment_date'),
          appointment_time: formData.get('appointment_time')
        },
        close: () => {
          // Handle print if save-print button was clicked
          if (clickedButton === 'save-print') {
            close()
            printData({
              ...formObject,
              doctor: (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor)
            })
          } else {
            close()
          }
        }
      }))
    } else {
      // Përditëso vetëm të dhënat bazë të pacientit, pa shtuar operation të ri
      dispatch(updatePatient({
        ...userdata,
        ...formObject,
        id: id || (selectedPatient && selectedPatient.id),
        operations: userdata?.operations || selectedPatient?.operations || []
      }));
      // Handle print if save-print button was clicked
      if (clickedButton === 'save-print') {
        close()
        printData({
          ...formObject,
          doctor: (isAdmin ? users : displayUsers).find(user => user.id === selectedDoctor)
        })
      } else {
        close();
      }
    }
  }

  const changeIndividualoperation = (operation, index) => {
    setServices((prev) => prev.map((op, i) => (i === index ? operation : op)))
  }

  const deleteInidividualOperation = (index) => {
    setServices((prev) => prev.filter((op, i) => i !== index))
  }

  // Funksion për të toggle akordeonët e shërbimeve
  const toggleServiceAccordion = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }))
  }

  // Funksion për të marrë shërbimet e pacientit të zgjedhur
  const getPatientServices = () => {
    if (!selectedPatient) return []
    
    console.log('Selected patient:', selectedPatient)
    console.log('All appointments from Redux:', appointments)
    
    // Filtro appointments që i përkasin pacientit të zgjedhur dhe janë për pulmonologji
    const patientAppointments = appointments?.filter(apt => {
      const belongsToPatient = apt.patient_id === selectedPatient.id
      const isPulmonology = apt.category === 'pulmonology'
      
      console.log('Checking appointment:', {
        appointment: apt,
        belongsToPatient,
        isPulmonology,
        shouldInclude: belongsToPatient && isPulmonology
      })
      
      return belongsToPatient && isPulmonology
    }) || []
    
    console.log('Filtered patient appointments:', patientAppointments)
    
    return patientAppointments.map((apt, index) => {
      // Merr emrin e shërbimit nga fusha të ndryshme
      const serviceName = apt.name || apt.operation_name || `Shërbim ${index + 1}`
      
      console.log('Processing appointment:', apt)
      console.log('Service name:', serviceName)
      
      return {
        id: apt.id || `appointment_${index}`,
        name: serviceName,
        date: apt.date,
        examination: apt.examination || selectedPatient.examination || '',
        therapy: apt.therapy || '',
        feedback: apt.feedback || selectedPatient.feedback || '',
        main_issue: apt.main_issue || selectedPatient.main_issue || '',
        service_cost: apt.service_cost || apt.price || 0,
        patient_pay: apt.patient_pay || apt.paid_amount || 0,
        difference: apt.difference || 0,
        // Shto informacionet mjekësore nga pacientët
        rtg_pa_pulmo: selectedPatient.rtg_pa_pulmo || '',
        ekg: selectedPatient.ekg || '',
        ta: selectedPatient.ta || '',
        spo2: selectedPatient.spo2 || '',
        dg: selectedPatient.dg || ''
      }
    })
  }

  // Funksion për të plotësuar automatikisht fushat e ekzaminimeve mjekësore
  const autofillMedicalFields = (service) => {
    setFormData(prev => ({
      ...prev,
      main_issue: service.main_issue || '',
      examination: service.examination || '',
      feedback: service.feedback || '',
      rtg_pa_pulmo: service.rtg_pa_pulmo || '',
      ekg: service.ekg || '',
      ta: service.ta || '',
      spo2: service.spo2 || '',
      dg: service.dg || ''
    }))
    
    // Plotëso edhe emrin e shërbimit në formë
    if (service.name && services.length > 0) {
      setServices(prev => prev.map((s, index) => 
        index === 0 ? { ...s, name: service.name } : s
      ))
    }
    
    // Mbyll akordeonin pas plotësimit të fushave
    setExpandedServices(prev => ({
      ...prev,
      [service.id]: false
    }))
  }

  // Rooms for selection - could be fetched from a database in a real application
  const rooms = ['Dhoma 1', 'Dhoma 2', 'Dhoma 3', 'Dhoma 4', 'Dhoma 5'];

  // Update total cost whenever services change
  useEffect(() => {
    const newTotalCost = services.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0);
    setServiceCostValue(newTotalCost);
    // For pulmonology, set patient pay equal to service cost by default
    if (category === 'pulmonology' && !isFreeService) {
      setPatientPayValue(newTotalCost);
      setDifferenceValue(0);
    } else {
      setDifferenceValue(newTotalCost - patientPayValue);
    }
  }, [services, patientPayValue, category, isFreeService]);

  // Shto këtë state në fillim të komponentit:
  const [isVeryLargeScreen, setIsVeryLargeScreen] = useState(window.innerWidth > 2000);

  useEffect(() => {
    const handleResize = () => setIsVeryLargeScreen(window.innerWidth > 2000);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={styles.popup_wrapper}>
      <div className={styles.form_header}>
        <h2 className={styles.header_text}>
          {id ? t('EDIT_NEW_PATIENT') : t('ADD_NEW_PATIENT')} ({t(category)?.toUpperCase()})
        </h2>
        <button className={styles.close_btn} onClick={close}>
          ✖
        </button>
      </div>
      <form className={styles.popup_from_container} onSubmit={submitData} autoComplete="off">
        <div className={styles.form_entry} ref={searchRef}>
          <label className={styles.entry_label} htmlFor='full_name'>
            {t('full_name')}
          </label>
          <div className={styles.search_container}>
            <input 
              value={formData.full_name}
              onChange={handleSearch}
              onFocus={() => {
                // Filter patients by category and show results
                const filteredPatients = patients.filter(patient => {
                  // If the patient has operations, check if any match the current category
                  if (patient.operations && patient.operations.length > 0) {
                    return patient.operations.some(op => op.category === category);
                  }
                  // If no operations, check the patient's category
                  return patient.category === category;
                });
                setSearchResults(filteredPatients)
                setShowResults(true)
              }}
              className={styles.entry_input} 
              required 
              type='text' 
              id='full_name' 
              name='full_name'
              placeholder={t('full_name')}
              autoComplete="off"
            />
            {showResults && searchResults.length > 0 && (
              <div className={styles.search_results}>
                {searchResults.map(patient => (
                  <div 
                    key={patient.id} 
                    className={styles.search_result_item}
                    onClick={() => handlePatientSelect(patient)}
                  >
                    {patient.full_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={styles.form_entry}>
          <label className={styles.entry_label} htmlFor='dob'>
            {t('date_of_birth')}
          </label>
          <input 
            value={formData.dob}
            onChange={(e) => {
              const year = e.target.value;
              setFormData(prev => ({ ...prev, dob: year }));
              // Store the full date in ISO format in a data attribute
              if (year) {
                const date = new Date(year, 0, 1); // Set to January 1st of the selected year
                e.target.dataset.isoDate = date.toISOString();
              }
            }}
            className={styles.entry_input} 
            type='number'
            id='dob' 
            name='dob'
            min="1900"
            max={new Date().getFullYear()}
            placeholder="YYYY"
            autoComplete="off"
          />
        </div>

        <div className={styles.form_entry} style={{ gridColumn: '1 / 2' }}>
          <label className={styles.entry_label} htmlFor='location'>
            {t('location')}
          </label>
          <input 
            value={formData.location}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, location: e.target.value }))
            }}
            className={styles.entry_input} 
            type='text' 
            id='location' 
            name='location' 
            autoComplete="off"
          />
        </div>

        {!id && (
          <>
            <div className={styles.form_entry} style={{ gridColumn: '2 / 3' }}>
              <label className={styles.entry_label}>
                {t('service_name')}
              </label>
              {services.map((service) => (
                <div key={service.id} className={styles.service_item} ref={el => menuRefs.current[service.id] = el}>
                  <div className={`${styles.op_selector_container} ${serviceMenuOpen[service.id] ? styles.active_menu : ''}`}>
                    <div className={styles.service_input_container}>
                      <input
                        type="text"
                        id={`service_${service.id}`}
                        name={`service_${service.id}`}
                        className={styles.selector_input}
                        placeholder={t('service_name')}
                        value={service.name}
                        onClick={() => setServiceMenuOpen({ [service.id]: !serviceMenuOpen[service.id] })}
                        readOnly
                        required={service.id === 1}
                      />
                      <button
                        type="button"
                        className={styles.add_service_btn}
                        onClick={addNewService}
                        title="Shto një shërbim tjetër"
                        style={{ marginLeft: 8 }}
                      >
                        + {t('add_fast_service') || 'Shto shërbim'}
                      </button>
                      {service.id !== 1 && (
                        <button
                          type="button"
                          className={styles.delete_service_btn}
                          onClick={() => deleteService(service.id)}
                          title="Fshi shërbimin"
                        >
                          ✖
                        </button>
                      )}
                    </div>
                    <div className={styles.op_menu_container}>
                      <div className={styles.op_menu_search}>
                        <input
                          className={styles.op_menu_search_input}
                          value={serviceSearchInput}
                          placeholder={t('search')}
                          onChange={(e) => setServiceSearchInput(e.target.value)}
                        />
                      </div>
                      <ul className={styles.op_menu_list_container}>
                        {filteredServices?.length ? (
                          filteredServices?.map((op) => (
                            <li
                              key={op.id}
                              className={styles.op_menu_item}
                              onClick={() => handleServiceSelect(op, service.id)}
                            >
                              <span className={styles.op_menu_text}>{op.op_name} - {op.price}€</span>
                            </li>
                          ))
                        ) : (
                          <li className={`${styles.op_menu_item} ${styles.disabled_entry}`} disabled>
                            <span className={styles.op_menu_text} style={{ textAlign: 'center', width: '100%' }}>
                              {t('no_services')}
                            </span>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add free service checkbox here, right after the service name field */}
            {category === 'pulmonology' && (
              <div className={styles.free_service_container}>
                <label className={styles.free_service_label}>
                  <input
                    type="checkbox"
                    checked={isFreeService}
                    onChange={(e) => {
                      setIsFreeService(e.target.checked)
                      if (e.target.checked) {
                        setServiceCostValue(0)
                        setPatientPayValue(0)
                        setDifferenceValue(0)
                      } else {
                        // Reset to original values if unchecking
                        const newTotalCost = services.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0)
                        setServiceCostValue(newTotalCost)
                        setPatientPayValue(newTotalCost) // Set patient pay equal to service cost
                        setDifferenceValue(0)
                      }
                    }}
                  />
                  <span className={styles.free_service_text}>
                    {t('free_service')}
                  </span>
                </label>
                <div className={styles.free_service_info}>
                  {t('free_service_info')}
                </div>
              </div>
            )}
            
            {/* Hidden cost-related fields */}
            <input
              type='hidden'
              id='service_cost'
              name='service_cost'
              value={serviceCostValue}
              onChange={(e) => {
                const cost = parseFloat(e.target.value) || 0
                setServiceCostValue(cost)
                setDifferenceValue(cost - patientPayValue)
              }}
            />
            <input
              type='hidden'
              id='patient_pay'
              name='patient_pay'
              value={patientPayValue}
              onChange={(e) => handlePatientPayChange(e.target.value)}
            />
            <input
              type='hidden'
              id='difference'
              name='difference'
              value={differenceValue}
              readOnly
            />
            
            {/* Room Selection - Only shown after adding an operation */}
            {category !== 'pulmonology' && category !== 'stomatology' && (
            <div className={styles.form_entry}>
              <label className={styles.entry_label}>
                {t('room')}
              </label>
              <select 
                className={styles.entry_input} 
                value={selectedRoom} 
                onChange={(e) => setSelectedRoom(e.target.value)}
              >
                <option value="">{t('select_room')}</option>
                {rooms.map((room) => (
                  <option key={room} value={room}>{room}</option>
                ))}
              </select>
            </div>
            )}

            {/* New fields for appointment date and time */}
            {category !== 'pulmonology' && (
            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor="appointment_date">
                {t('appointment_date')}
              </label>
              <input 
                className={styles.entry_input} 
                type="date" 
                id="appointment_date" 
                name="appointment_date" 
                defaultValue={new Date().toISOString().split('T')[0]}
                lang="en-GB"
                onFocus={(e) => {
                  // Set the date format to DD/MM/YYYY when focused
                  if (e.target.value) {
                    const date = new Date(e.target.value);
                    e.target.value = date.toLocaleDateString('en-GB');
                  }
                }}
                onBlur={(e) => {
                  // Convert back to YYYY-MM-DD for the date input
                  if (e.target.value) {
                    const [day, month, year] = e.target.value.split('/');
                    if (day && month && year) {
                      e.target.value = `${year}-${month}-${day}`;
                    }
                  }
                }}
              />
            </div>
            )}
            {category !== 'pulmonology' && (
            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor="appointment_time">
                {t('appointment_time')}
              </label>
              <select className={styles.entry_input} id="appointment_time" name="appointment_time">
                {arrayOfTimesGeneral.map((time) => (
                  <option key={time.value} value={time.displayValue}>{time.displayValue}</option>
                ))}
              </select>
            </div>
            )}

            {/* Add doctor selection after appointment time */}
            <div className={styles.form_entry}>
              <label className={styles.entry_label}>
                {t('select_doctor')}
              </label>
              <select 
                className={styles.entry_input} 
                value={selectedDoctor} 
                onChange={(e) => setSelectedDoctor(e.target.value)}
                required
              >
                <option value="">{t('select_doctor')}</option>
                {(isAdmin ? users : displayUsers)?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName} ({user.role || 'no role'})
                  </option>
                ))}
              </select>
            </div>

            {/* Akordeonët e shërbimeve për pacientët ekzistues - vetëm për pulmonologji */}
            {category === 'pulmonology' && selectedPatient && (
              <div className={styles.form_entry} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.entry_label}>
                  {t('current_service')} - {selectedPatient.full_name}
                </label>
                <div className={styles.patient_services_container}>
                  {getPatientServices().length > 0 ? (
                    getPatientServices().map((service) => (
                      <div key={service.id} className={styles.service_accordion}>
                        <div 
                          className={`${styles.accordion_header} ${expandedServices[service.id] ? styles.expanded : ''}`}
                          onClick={() => toggleServiceAccordion(service.id)}
                        >
                          <span className={styles.service_name}>{service.name}</span>
                          <span className={styles.accordion_icon}>
                            {expandedServices[service.id] ? '▼' : '▶'}
                          </span>
                        </div>
                        {expandedServices[service.id] && (
                          <div className={styles.accordion_content}>
                            <div className={styles.service_details}>
                              <div className={styles.service_info}>
                                <div className={styles.info_row}>
                                  <span className={styles.info_label}>{t('date')}:</span>
                                  <span className={styles.info_value}>
                                    {service.date ? new Date(service.date).toLocaleDateString('sq-AL') : t('no_time')}
                                  </span>
                                </div>
                                <div className={styles.info_row}>
                                  <span className={styles.info_label}>{t('service_cost')}:</span>
                                  <span className={styles.info_value}>{service.service_cost}€</span>
                                </div>
                                <div className={styles.info_row}>
                                  <span className={styles.info_label}>{t('patient_payment')}:</span>
                                  <span className={styles.info_value}>{service.patient_pay}€</span>
                                </div>
                                <div className={styles.info_row}>
                                  <span className={styles.info_label}>{t('difference')}:</span>
                                  <span className={styles.info_value}>{service.difference}€</span>
                                </div>
                              </div>
                              
                              {/* Ekzaminimet mjekësore */}
                              <div className={styles.medical_examinations}>
                                <div className={styles.examinations_header}>
                                  <h4 className={styles.examinations_title}>{t('medical_examinations')}</h4>
                                  <button
                                    type="button"
                                    className={styles.autofill_button}
                                    onClick={() => autofillMedicalFields(service)}
                                    title="Plotëso automatikisht fushat e ekzaminimeve mjekësore"
                                  >
                                    {t('autofill_fields')}
                                  </button>
                                </div>
                                
                                {service.main_issue && (
                                  <div className={styles.examination_item}>
                                    <span className={styles.examination_label}>{t('main_issue')}:</span>
                                    <span className={styles.examination_value}>{service.main_issue}</span>
                                  </div>
                                )}
                                
                                {service.examination && (
                                  <div className={styles.examination_item}>
                                    <span className={styles.examination_label}>{t('examination')}:</span>
                                    <span className={styles.examination_value}>{service.examination}</span>
                                  </div>
                                )}
                                
                                {service.therapy && (
                                  <div className={styles.examination_item}>
                                    <span className={styles.examination_label}>{t('therapy')}:</span>
                                    <span className={styles.examination_value}>{service.therapy}</span>
                                  </div>
                                )}
                                
                                {service.feedback && (
                                  <div className={styles.examination_item}>
                                    <span className={styles.examination_label}>{t('feedback')}:</span>
                                    <span className={styles.examination_value}>{service.feedback}</span>
                                  </div>
                                )}
                                
                                {service.rtg_pa_pulmo && (
                                  <div className={styles.examination_item}>
                                    <span className={styles.examination_label}>{t('rtg_pa_pulmo')}:</span>
                                    <span className={styles.examination_value}>{service.rtg_pa_pulmo}</span>
                                  </div>
                                )}
                                
                                {service.ekg && (
                                  <div className={styles.examination_item}>
                                    <span className={styles.examination_label}>{t('ekg')}:</span>
                                    <span className={styles.examination_value}>{service.ekg}</span>
                                  </div>
                                )}
                                
                                {service.ta && (
                                  <div className={styles.examination_item}>
                                    <span className={styles.examination_label}>{t('ta')}:</span>
                                    <span className={styles.examination_value}>{service.ta}</span>
                                  </div>
                                )}
                                
                                {service.spo2 && (
                                  <div className={styles.examination_item}>
                                    <span className={styles.examination_label}>{t('spo2')}:</span>
                                    <span className={styles.examination_value}>{service.spo2}</span>
                                  </div>
                                )}
                                
                                {service.dg && (
                                  <div className={styles.examination_item}>
                                    <span className={styles.examination_label}>{t('dg')}:</span>
                                    <span className={styles.examination_value}>{service.dg}</span>
                                  </div>
                                )}
                                
                                {!service.main_issue && !service.examination && !service.therapy && !service.feedback && 
                                 !service.rtg_pa_pulmo && !service.ekg && !service.ta && !service.spo2 && !service.dg && (
                                  <div className={styles.no_examinations}>
                                    {t('no_examination')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className={styles.no_services}>
                      {t('no_services')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Teeth Selection for Stomatology - Only shown after adding an operation */}
            {category === 'stomatology' && false && (
              <div className={styles.form_entry}>
                <label className={styles.entry_label}>
                  {t('teeth_selection')}
                </label>
                <div className={styles.teeth_selection_container}>
                  <div className={styles.teeth_buttons}>
                    <button
                      type="button"
                      className={`${styles.teeth_button} ${selectedType === 'all' ? styles.active : ''}`}
                      onClick={() => setSelectedType('all')}
                    >
                      {t('all')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.teeth_button} ${selectedType === 'adult' ? styles.active : ''}`}
                      onClick={() => setSelectedType('adult')}
                    >
                      {t('adult')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.teeth_button} ${selectedType === 'child' ? styles.active : ''}`}
                      onClick={() => setSelectedType('child')}
                    >
                      {t('child')}
                    </button>
                  </div>
                  <div className={styles.teeth_chart}>
                    <SVGMouth selectedType={selectedType} selectedTeeth={selectedTeeth} onToothclick={onToothclick} />
                  </div>
                </div>
              </div>
            )}
            
            {/* Medical Examination Fields - Only show if not stomatology */}
            <div className={styles.form_entry + ' ' + styles.full_width}>
              <h3 className={styles.section_header}>{t('medical_examinations')}</h3>
            </div>
            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor='main_issue'>
                {t('main_issue')}
              </label>
              <textarea 
                className={styles.entry_input} 
                type='text' 
                id='main_issue' 
                name='main_issue' 
                value={formData.main_issue}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, main_issue: e.target.value }))
                }}
                onInput={handleTextareaInput}
              />
            </div>
            {category !== 'stomatology' && (
              <>
                <div className={styles.form_entry}>
                  <label className={styles.entry_label} htmlFor='rtg_pa_pulmo'>
                    {t('rtg_pa_pulmo')}
                  </label>
                  <textarea 
                    className={styles.entry_input} 
                    type='text' 
                    id='rtg_pa_pulmo' 
                    name='rtg_pa_pulmo'
                    value={formData.rtg_pa_pulmo}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, rtg_pa_pulmo: e.target.value }))
                    }}
                    onInput={handleTextareaInput}
                  />
                </div>
                <div className={styles.form_entry}>
                  <label className={styles.entry_label} htmlFor='ekg'>
                    {t('ekg')}
                  </label>
                  <textarea 
                    className={styles.entry_input} 
                    type='text' 
                    id='ekg' 
                    name='ekg'
                    value={formData.ekg}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, ekg: e.target.value }))
                    }}
                    onInput={handleTextareaInput}
                  />
                </div>
                <div className={styles.form_entry}>
                  <label className={styles.entry_label} htmlFor='ta'>
                    {t('ta')}
                  </label>
                  <textarea 
                    className={styles.entry_input} 
                    type='text' 
                    id='ta' 
                    name='ta'
                    value={formData.ta}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, ta: e.target.value }))
                    }}
                    onInput={handleTextareaInput}
                  />
                </div>
                <div className={styles.form_entry}>
                  <label className={styles.entry_label} htmlFor='spo2'>
                    {t('spo2')}
                  </label>
                  <textarea 
                    className={styles.entry_input} 
                    type='text' 
                    id='spo2' 
                    name='spo2'
                    value={formData.spo2}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, spo2: e.target.value }))
                    }}
                    onInput={handleTextareaInput}
                  />
                </div>
                <div className={styles.form_entry}>
                  <label className={styles.entry_label} htmlFor='dg'>
                    {t('dg')}
                  </label>
                  <textarea 
                    className={styles.entry_input} 
                    type='text' 
                    id='dg' 
                    name='dg'
                    value={formData.dg}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, dg: e.target.value }))
                    }}
                    onInput={handleTextareaInput}
                  />
                </div>
              </>
            )}

            {category !== 'stomatology' && (
              <div className={styles.form_entry}>
                <label className={styles.entry_label} htmlFor='examination'>
                  {t('examination')}
                </label>
                <textarea 
                  className={styles.entry_input} 
                  type='text' 
                  id='examination' 
                  name='examination'
                  value={formData.examination}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, examination: e.target.value }))
                  }}
                  onInput={handleTextareaInput}
                />
              </div>
            )}

            {category !== 'pulmonology' && (
              <div className={styles.form_entry}>
                <label className={styles.entry_label} htmlFor='feedback'>
                  {t('feedback')}
                </label>
                <textarea 
                  className={styles.entry_input} 
                  type='text' 
                  id='feedback' 
                  name='feedback'
                  value={formData.feedback}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, feedback: e.target.value }))
                  }}
                  onInput={handleTextareaInput}
                />
              </div>
            )}

            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor='therapy'>
                {t('therapy')}
              </label>
              <textarea 
                className={styles.entry_input} 
                type='textarea'
                id='therapy' 
                name='therapy' 
                onInput={handleTextareaInput}
              />
            </div>
          </>
        )}
        <div className={`${styles.form_entry} ${styles.checkbox_entry}`}>
          <label className={styles.entry_label} htmlFor='show_additional'>
            {t('show_additional_info')}
          </label>
          <input
            className={styles.entry_input}
            type='checkbox'
            id='show_additional'
            name='show_additional'
            onChange={() => setShowAdditional(!showAdditional)}
            checked={showAdditional}
          />
        </div>
        {showAdditional && (
          <div className={styles.additional_info_container}>
            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor='email'>
                {t('email')}
              </label>
              <input 
                value={formData.email}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, email: e.target.value }))
                }}
                className={styles.entry_input} 
                type='email' 
                id='email' 
                name='email'
                autoComplete="off"
              />
            </div>
            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor='phone'>
                {t('phone')}
              </label>
              <input 
                value={formData.phone}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, phone: e.target.value }))
                }}
                className={styles.entry_input} 
                type='phone' 
                id='phone' 
                name='phone'
                autoComplete="off"
              />
            </div>
            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor='nid'>
                ID
              </label>
              <input 
                value={formData.nid}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, nid: e.target.value }))
                }}
                className={styles.entry_input} 
                type='text' 
                id='nid' 
                name='nid'
                autoComplete="off"
              />
            </div>
          </div>
        )}
        <div className={styles.form_row} style={{ marginBottom: '12px' }}>
          <button type='submit' id='save-close' className={styles.form_submit}>
            {t('save_close')}
          </button>
          {!id && (
            <button type='submit' id='save-print' className={styles.form_submit}>
              {t('save_print')}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

export default index